import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, DatePicker, Space, Tooltip, Typography } from "antd";
import dayjs from "dayjs";

import { useEffect, useRef, useState } from "react";

const { Text } = Typography;
const HEIGHT = 24;
const WIDTH = 120;
const DISPLAY_FMT = "YYYY-MM-DD";
// Ant Design's warning amber, fading back to exactly how the field looked
const GLOW = [
  {
    borderColor: "#faad14",
    backgroundColor: "#fffbe6",
    boxShadow: "0 0 8px 2px rgba(250, 173, 20, 0.6)",
  },
  {
    borderColor: "transparent",
    backgroundColor: "transparent",
    boxShadow: "0 0 8px 2px rgba(250, 173, 20, 0)",
  },
];

// `attention` goes up each time the page needs a date that isn't set yet (Compose Email). The
// field then takes focus and glows briefly; nothing about it stays changed.
export default function InlineEditableCoverageDate({
  value,
  onSave,
  disabled,
  attention = 0,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ? dayjs(value) : dayjs()); // default to today if empty
  const wrapRef = useRef(null);
  const pickerRef = useRef(null);
  const displayRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ? dayjs(value) : dayjs());
  }, [value, editing]);

  useEffect(() => {
    if (!attention || value) return;
    const field = displayRef.current;
    field?.focus();
    field?.animate?.(GLOW, { duration: 800, iterations: 2, easing: "ease-in-out" });
  }, [attention, value]);

  const handleSave = () => {
    setEditing(false);
    const formatted = draft ? dayjs(draft).format(DISPLAY_FMT) : undefined;
    if (formatted === value) return;
    onSave?.(formatted);
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft(value ? dayjs(value) : dayjs());
  };

  const handleBlur = (e) => {
    const next = e.relatedTarget;
    if (!wrapRef.current) return handleCancel();
    if (next && wrapRef.current.contains(next)) return;
    handleCancel();
  };

  if (editing && !disabled) {
    return (
      <div
        ref={wrapRef}
        onBlur={handleBlur}
        style={{ display: "inline-block", lineHeight: 0 }}
      >
        <Space.Compact size="small">
          <DatePicker
            ref={pickerRef}
            size="small"
            allowClear={false} // no clear button
            value={draft}
            onChange={(d) => setDraft(d || draft)}
            style={{ width: WIDTH, height: HEIGHT }}
          />
          <Button
            type="default"
            style={{
              marginLeft: 1,
              height: HEIGHT,
              color: "#389e0d",
              background: "#f6ffed",
              borderColor: "#b7eb8f",
            }}
            icon={<CheckOutlined />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSave}
          />
          <Button
            type="default"
            style={{
              height: HEIGHT,
              color: "#cf1322",
              background: "#fff1f0",
              borderColor: "#ffa39e",
            }}
            icon={<CloseOutlined />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCancel}
          />
        </Space.Compact>
      </div>
    );
  }

  return (
    <Tooltip title={disabled ? "" : "Click to edit"}>
      <span
        ref={displayRef}
        role={disabled ? "text" : "button"}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " "))
            setEditing(true);
        }}
        style={{
          width: WIDTH,
          height: HEIGHT,
          display: "inline-flex",
          alignItems: "center",
          padding: "0 7px",
          border: "1px solid transparent",
          borderRadius: 0,
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = "#d9d9d9";
          e.currentTarget.style.background = "#f5f5f5";
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = "transparent";
          e.currentTarget.style.background = "transparent";
        }}
      >
        <Text>{value ? dayjs(value).format(DISPLAY_FMT) : "TBD"}</Text>
      </span>
    </Tooltip>
  );
}
